<template>
  <UPageSection
    :dir="dir"
    :title="title"
    :ui="{
      root: 'min-w-0 overflow-x-clip',
      container: 'w-full max-w-full min-w-0 !gap-0 py-10 sm:py-14 lg:grid-cols-1 lg:py-10',
      title: 'max-w-full text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl text-center',
    }"
  >
    <div class="min-w-0 w-full">
      <ClientOnly v-if="items.length">
        <div class="block-testimonials__swiper my-8 min-w-0 w-full sm:my-12">
          <div
            v-if="showNavigation"
            class="mb-8 flex items-center gap-2"
            :class="dir === 'rtl' ? 'justify-end' : 'justify-start'"
          >
            <button
              type="button"
              class="block-testimonials__nav-btn block-testimonials__nav-prev border-gray-200 bg-white hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800"
              :aria-label="navPrevLabel"
            >
              <UIcon
                :name="dir === 'rtl' ? 'i-lucide-chevron-right' : 'i-lucide-chevron-left'"
                class="size-4"
              />
            </button>
            <button
              type="button"
              class="block-testimonials__nav-btn block-testimonials__nav-next border-gray-200 bg-white hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800"
              :aria-label="navNextLabel"
            >
              <UIcon
                :name="dir === 'rtl' ? 'i-lucide-chevron-left' : 'i-lucide-chevron-right'"
                class="size-4"
              />
            </button>
          </div>

          <Swiper
            :modules="swiperModules"
            :dir="dir"
            :slides-per-view="slidesPerView"
            :space-between="spaceBetween"
            :breakpoints="swiperBreakpoints"
            :navigation="navigationOptions"
            :pagination="paginationOptions"
            :autoplay="autoplayOptions"
            :loop="canLoop"
            class="equal-h-swiper block-testimonials-swiper overflow-hidden"
          >
            <SwiperSlide
              v-for="(item, index) in items"
              :key="index"
              class="h-auto py-2"
            >
              <article :class="['testimonial-card', `testimonial-card--${cardStyle}`]">
                <UIcon
                  name="i-lucide-quote"
                  class="testimonial-card__mark"
                  aria-hidden="true"
                />
                <p class="testimonial-card__text">
                  {{ item.comment }}
                </p>
                <footer class="testimonial-card__foot">
                  <span
                    class="testimonial-card__avatar"
                    aria-hidden="true"
                  >{{ initialOf(item.name) }}</span>
                  <span class="testimonial-card__name">{{ item.name }}</span>
                </footer>
              </article>
            </SwiperSlide>
          </Swiper>
        </div>
        <template #fallback>
          <div class="grid items-stretch gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <article
              v-for="(item, index) in items"
              :key="index"
              :class="['testimonial-card', `testimonial-card--${cardStyle}`]"
            >
              <p class="testimonial-card__text">
                {{ item.comment }}
              </p>
              <footer class="testimonial-card__foot">
                <span class="testimonial-card__name">{{ item.name }}</span>
              </footer>
            </article>
          </div>
        </template>
      </ClientOnly>

      <p
        v-else
        class="text-center text-sm text-muted"
      >
        {{ emptyLabel }}
      </p>
    </div>
  </UPageSection>
</template>

<script setup lang="ts">
import { Autoplay, Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'

export type TestimonialItem = {
  name?: string
  comment?: string
}

const dir = useDir()
const pageLocale = useState<string>('page-locale', () => 'fa')

const props = withDefaults(
  defineProps<{
    title?: string
    items?: TestimonialItem[]
    cardStyle?: 'classic' | 'outlined' | 'minimal' | 'accent'
    autoplay?: boolean
    autoplayDelay?: number
    loop?: boolean
    builderPreview?: boolean
    previewDevice?: 'desktop' | 'mobile'
  }>(),
  {
    items: () => [],
    autoplay: true,
    autoplayDelay: 6000,
    loop: true,
  },
)

const swiperModules = [Pagination, Navigation, Autoplay]
const itemCount = computed(() => props.items.length)
const cardStyle = computed(() => {
  const allowedStyles = ['classic', 'outlined', 'minimal', 'accent'] as const
  return allowedStyles.find(style => style === props.cardStyle) ?? 'classic'
})

const spaceBetween = computed(() => (props.previewDevice === 'mobile' ? 16 : 24))

const slidesPerView = computed(() => {
  if (props.previewDevice === 'mobile') return 1
  if (props.previewDevice === 'desktop') return Math.min(2, Math.max(1, itemCount.value))
  return 1
})

const swiperBreakpoints = computed(() => {
  if (props.previewDevice) return undefined
  const max = Math.max(1, itemCount.value)
  return {
    0: { slidesPerView: 1, spaceBetween: 16 },
    640: { slidesPerView: Math.min(2, max), spaceBetween: 24 },
    1024: { slidesPerView: Math.min(3, max), spaceBetween: 28 },
  }
})

const showNavigation = computed(() => {
  if (props.previewDevice === 'mobile') return itemCount.value > 1
  if (props.previewDevice === 'desktop') return itemCount.value > 2
  return itemCount.value > 1
})

const navigationOptions = computed(() => {
  if (!showNavigation.value) return false
  return {
    prevEl: '.block-testimonials__nav-prev',
    nextEl: '.block-testimonials__nav-next',
  }
})

const navPrevLabel = computed(() =>
  pageLocale.value === 'fa' ? 'نظر قبلی' : 'Previous review',
)

const navNextLabel = computed(() =>
  pageLocale.value === 'fa' ? 'نظر بعدی' : 'Next review',
)

const paginationOptions = computed(() => ({
  clickable: true,
  dynamicBullets: true,
}))

const autoplayDelay = computed(() => {
  const value = Number(props.autoplayDelay)
  if (!Number.isFinite(value) || value < 1000) return 6000
  return Math.min(60000, Math.round(value))
})

const canLoop = computed(() => Boolean(props.loop) && itemCount.value > 1)

const autoplayOptions = computed(() => {
  if (!props.autoplay || itemCount.value < 2) return false
  return {
    delay: autoplayDelay.value,
    disableOnInteraction: true,
    pauseOnMouseEnter: true,
    stopOnLastSlide: !props.loop,
  }
})

const emptyLabel = computed(() =>
  pageLocale.value === 'fa' ? 'هنوز نظری ثبت نشده است.' : 'No testimonials yet.',
)

function initialOf(name?: string) {
  const trimmed = (name ?? '').trim()
  return trimmed ? trimmed.charAt(0) : '•'
}
</script>

<style scoped>
.testimonial-card {
  --testimonial-card-background: var(--ui-bg-elevated, var(--ui-bg, #fff));
  --testimonial-card-border: var(--ui-border, #e2e8f0);

  display: flex;
  height: 100%;
  min-height: 100%;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.75rem 1.5rem 1.5rem;
  border-radius: 1.75rem;
  border: 1px solid color-mix(in srgb, var(--ui-primary, #2563eb) 16%, var(--testimonial-card-border));
  background:
    linear-gradient(180deg, color-mix(in srgb, var(--ui-primary, #2563eb) 7%, var(--testimonial-card-background)), var(--testimonial-card-background) 42%);
  box-shadow: 0 16px 40px color-mix(in srgb, var(--ui-text, #0f172a) 6%, transparent);
}

.testimonial-card--outlined {
  border: 2px solid color-mix(in srgb, var(--ui-primary, #2563eb) 55%, var(--testimonial-card-border));
  background: var(--ui-bg, var(--testimonial-card-background));
  box-shadow: none;
}

.testimonial-card--minimal {
  border: 0;
  border-radius: 0;
  border-block-end: 1px solid var(--testimonial-card-border);
  background: transparent;
  box-shadow: none;
}

.testimonial-card--accent {
  border-inline-start: 4px solid var(--ui-primary, #2563eb);
  border-color: color-mix(in srgb, var(--ui-primary, #2563eb) 35%, var(--testimonial-card-border));
  background: color-mix(in srgb, var(--ui-primary, #2563eb) 12%, var(--testimonial-card-background));
  box-shadow: none;
}

.testimonial-card__mark {
  width: 1.75rem;
  height: 1.75rem;
  color: var(--ui-primary, #2563eb);
  opacity: 0.85;
}

.testimonial-card__text {
  margin: 0;
  flex: 0 0 auto;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.85;
  color: var(--ui-text, #1e293b);
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: calc(1.85em * 4);
}

.testimonial-card__foot {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: auto;
  padding-top: 0.25rem;
}

.testimonial-card__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  flex-shrink: 0;
  border-radius: 9999px;
  background: color-mix(in srgb, var(--ui-primary, #2563eb) 14%, var(--testimonial-card-background));
  color: var(--ui-primary, #2563eb);
  font-size: 0.95rem;
  font-weight: 700;
}

.testimonial-card__name {
  min-width: 0;
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.4;
  color: var(--ui-text-highlighted, var(--ui-text, #0f172a));
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.block-testimonials-swiper :deep(.swiper-pagination) {
  position: relative !important;
  bottom: auto !important;
  margin-top: 1rem;
  width: 100%;
}

.block-testimonials-swiper :deep(.swiper-pagination-bullet-active) {
  background-color: var(--ui-primary);
}

.block-testimonials__nav-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 9999px;
  border-width: 1px;
  border-style: solid;
  color: var(--ui-primary);
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
}

.block-testimonials__nav-btn.swiper-button-disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.block-testimonials__nav-btn::after {
  display: none;
}

.block-testimonials__swiper :deep(.block-testimonials__nav-btn.swiper-button-prev),
.block-testimonials__swiper :deep(.block-testimonials__nav-btn.swiper-button-next) {
  position: static;
  inset: auto;
  margin: 0;
}
</style>
